/**
 * TailoringSelectionCard — the select-then-evaluate gate for carrying own insights over from a
 * previous DMA (PLT-4968).
 *
 * The chat-only tool `request_tailoring_selection` validates a list of the user's previous-DMA
 * tailorings and echoes it back as the tool result. This card renders that list as a checklist so
 * the user decides WHICH of them the agent should check against the current DMA — BEFORE the agent
 * evaluates any of them.
 *
 * On confirm, the card does NOT write anything. It re-enters the agent conversation by appending a
 * new user turn via `runtime.thread.append(...)` — the same continuation pattern `suggest_followups`
 * and the welcome chips use. The appended message names the kept tailorings (summaries + their
 * selection ids) so the agent can evaluate exactly that subset and propose the worthwhile ones via
 * `propose_tailoring_batch` / `<TailoringBatchCard>`.
 *
 * PLT-7899: rows use the proposal card's layout, headed "<action>: <IRO name>" with the matter
 * beneath, from the `action`, `iroName` and `matterName` the agent copies from
 * `list_importable_tailorings`. An item without them falls back to its `summary`.
 *
 * Restricted-company handling: the list was produced by `list_importable_tailorings`, which enforces
 * per-variant read access server-side; this card is a pure presentation + selection layer and never
 * re-fetches company data or writes anything.
 */
import { useState } from 'react';
import { useAssistantRuntime } from '@assistant-ui/react';
import { Button } from '@upright/ui/common/generic/components/button';
import { DataCard } from '@upright/ui/common/generic/components/card';
import { Stack } from '@upright/ui/common/generic/components/layout';
import { Link } from '@upright/ui/common/generic/components/link';
import { Heading, Text } from '@upright/ui/common/generic/components/typography';
import { composerState } from './upright-chat-shared';
import { ChatMarkdown } from './upright-chat-markdown';
import { OwnInsightRow, OwnInsightRows, matterLine, ownInsightHeadline } from './own-insight-row';
import styles from './own-insight-cards.module.css';

// ---------------------------------------------------------------------------
// Types — the shape the chat-only tool echoes back. Kept inline (not imported
// from the backend module) so the UI bundle does not pull in zod. Structurally
// matches `RequestTailoringSelectionInput` in
// `mcp/tools/request-tailoring-selection.ts`.
// ---------------------------------------------------------------------------

interface SelectionItem {
  itemId: string;
  summary: string;
  action?: string;
  iroName?: string;
  matterName?: string;
  detail?: string;
  preChecked?: boolean;
}

export interface TailoringSelectionCardProps {
  companyId: string;
  selectionId: string;
  priorDmaLabel?: string;
  promptMarkdown?: string;
  items: SelectionItem[];
}

/** Rows shown before "Show N more"; the common case is to check them all, so the rest wait. */
const VISIBLE_ROWS = 4;

export function TailoringSelectionCard({ priorDmaLabel, promptMarkdown, items }: TailoringSelectionCardProps) {
  const runtime = useAssistantRuntime();

  // Default true: pre-select all so the user opts OUT of the ones they don't want checked.
  const [selected, setSelected] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    for (const item of items) initial[item.itemId] = item.preChecked ?? true;
    return initial;
  });
  // Once confirmed, the card locks into a short receipt; the agent's next turn does the evaluating.
  const [confirmed, setConfirmed] = useState<{ keptCount: number } | null>(null);
  const [showAll, setShowAll] = useState(false);

  const selectedItems = items.filter(item => selected[item.itemId]);
  const selectedCount = selectedItems.length;
  const fromLabel = priorDmaLabel ? `from ${priorDmaLabel}` : 'from the previous DMA';

  const setAll = (value: boolean) => {
    setSelected(prev => {
      const next = { ...prev };
      for (const item of items) next[item.itemId] = value;
      return next;
    });
  };

  const confirm = () => {
    if (confirmed !== null) return;
    // Tagged like a follow-up action (a UI-driven re-entry, as the follow-up chips are) so chat-usage
    // analytics attribute it consistently.
    composerState.pendingSource = 'followup_suggestion';
    runtime.thread.append(buildContinuationPrompt(selectedItems, items.length, priorDmaLabel));
    setConfirmed({ keptCount: selectedItems.length });
  };

  if (confirmed !== null) {
    return (
      <DataCard>
        <Stack gap="xs">
          <Heading level={3} size="lg" weight="bold" color="gray-900">
            {confirmed.keptCount === 0 ? 'Nothing carried over' : `Checking ${insightsCountLabel(confirmed.keptCount)}`}
          </Heading>
          <Text size="sm" height="xs" color="gray-600">
            {confirmed.keptCount === 0
              ? `No own insights ${fromLabel} were selected.`
              : `${fromLabel[0].toUpperCase()}${fromLabel.slice(1)}, against the current DMA`}
          </Text>
        </Stack>
      </DataCard>
    );
  }

  const visibleItems = showAll ? items : items.slice(0, VISIBLE_ROWS);
  const hiddenCount = items.length - visibleItems.length;

  return (
    <DataCard>
      <Stack gap="lg">
        <Stack gap="xs">
          <Heading level={3} size="lg" weight="bold" color="gray-900">
            {`Carry over own insights ${fromLabel}?`}
          </Heading>
          <Text size="sm" height="xs" color="gray-600">{`${selectedCount} of ${items.length} selected`}</Text>
        </Stack>
        {promptMarkdown && promptMarkdown.length > 0 ? (
          <ChatMarkdown>{promptMarkdown}</ChatMarkdown>
        ) : (
          <Text size="md" height="sm" color="gray-800">
            I’ll check each one you keep against the current DMA and propose the ones that still hold.
          </Text>
        )}
        <Stack gap="sm">
          {items.length > 1 && (
            <div className={styles.groupHead}>
              <span className={styles.groupActions}>
                <Text size="sm" height="xs">
                  <Link
                    onPress={() => {
                      setAll(true);
                    }}
                  >
                    Select all
                  </Link>
                </Text>
                <Text size="sm" height="xs">
                  <Link
                    onPress={() => {
                      setAll(false);
                    }}
                  >
                    Clear
                  </Link>
                </Text>
              </span>
            </div>
          )}
          <OwnInsightRows>
            {visibleItems.map(item => (
              <OwnInsightRow
                key={item.itemId}
                headline={ownInsightHeadline(item, item.summary)}
                meta={item.action ? matterLine(item.matterName, item.detail) : item.detail}
                selection={{
                  isSelected: !!selected[item.itemId],
                  isDisabled: false,
                  onChange: value => {
                    setSelected(prev => ({ ...prev, [item.itemId]: value }));
                  },
                }}
              />
            ))}
          </OwnInsightRows>
          {hiddenCount > 0 && (
            <Text size="sm" height="xs">
              <Link
                onPress={() => {
                  setShowAll(true);
                }}
              >
                {`Show ${hiddenCount} more`}
              </Link>
            </Text>
          )}
        </Stack>
        {/* Secondary action first, primary last. */}
        <Stack row gap="sm" align="center" wrap>
          <Button
            variant="outlined"
            size="small"
            onPress={() => {
              setAll(false);
              composerState.pendingSource = 'followup_suggestion';
              runtime.thread.append(buildContinuationPrompt([], items.length, priorDmaLabel));
              setConfirmed({ keptCount: 0 });
            }}
          >
            Skip
          </Button>
          <Button variant="contained" size="small" isDisabled={selectedCount === 0} onPress={confirm}>
            {`Check ${insightsCountLabel(selectedCount)}`}
          </Button>
        </Stack>
      </Stack>
    </DataCard>
  );
}

function insightsCountLabel(count: number): string {
  return count === 1 ? '1 own insight' : `${count} own insights`;
}

/**
 * Build the user turn appended on confirm. It names the kept tailorings by summary (readable in the
 * transcript) and lists their selection ids (so the agent maps back to exactly the right prior
 * tailorings, unambiguously, even if two summaries collide). The "do not evaluate any other" clause
 * mirrors the tool's interpretation so the gate holds: unselected tailorings are out of scope.
 */
function buildContinuationPrompt(kept: SelectionItem[], total: number, priorDmaLabel?: string): string {
  const fromLabel = priorDmaLabel ? `my ${priorDmaLabel}` : 'my previous DMA';
  if (kept.length === 0) {
    return (
      `I don't want to migrate any of the tailorings from ${fromLabel}. ` +
      'Skip the migration and continue with my uploaded files, if any.'
    );
  }
  const lines = kept.map(it => `- ${it.summary}${it.matterName ? ` (${it.matterName})` : ''}`).join('\n');
  const skipped = total - kept.length;
  const ids = kept.map(it => it.itemId).join(', ');
  return (
    `I've picked which tailorings from ${fromLabel} to consider migrating. Evaluate ONLY these against the current DMA and ` +
    'propose the ones that still hold up — do not evaluate or propose any other previous-DMA tailoring' +
    (skipped > 0 ? ` (${skipped} left unselected)` : '') +
    `:\n${lines}\n\n(selection ids kept: ${ids})`
  );
}
